import { DeepPartial } from "@chakra-ui/react";

import {
  Create2dFloodModelSchema,
  CreateBasicHydroDataSchema,
  DefineStudyAreaSchema,
  ProcessTagSchemas,
} from "@nens/rana/process-types";
import { FormDerivationRulesFor } from "@nens/ranalib";

import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { ProcessRunFormData } from "../types/process-run-form-data";

// Look at /nens-frontends/libs/ranalib/src/lib/rana-form-derived-state/helpers/flatten-derived-rules.ts to see how the derived rules work

const getStudyAreaNameInDefineStudyArea = (
  data: DeepPartial<DefineStudyAreaSchema>
): string | undefined => {
  const area = data.inputs?.area;
  if (!area) return;

  // Draw on map
  if ("polygon" in area) {
    const { polygon } = area;
    if (
      polygon &&
      "properties" in polygon &&
      typeof polygon.properties === "object" &&
      polygon.properties &&
      "placeName" in polygon.properties &&
      typeof polygon.properties.placeName === "string"
    )
      return polygon.properties.placeName;
  }

  // Standard boundaries
  if ("selection" in area) {
    const { selection } = area;
    if (
      selection &&
      "featureNames" in selection &&
      Array.isArray(selection.featureNames) &&
      !!selection.featureNames[0]
    )
      return `${selection.featureNames[0]}${
        selection.featureNames.length > 1 ? ` ${selection.featureNames.length}` : ""
      }`;
  }

  // Select your own file
  if ("fileBoundary" in area) {
    const { fileBoundary } = area;
    if (fileBoundary && fileBoundary.id) return getNameFromFileId(fileBoundary.id);
  }
};

const getStudyAreaNameFromInputs = (
  data: DeepPartial<Create2dFloodModelSchema> | DeepPartial<CreateBasicHydroDataSchema>
): string | undefined => {
  const id = data?.inputs?.studyArea?.id;
  if (!id) return;
  return getNameFromFileId(id)?.split(".")[0];
};

const getSchematisationNameFromStudyArea = (
  data: DeepPartial<Create2dFloodModelSchema>
): string | undefined => {
  const studyAreaName = getStudyAreaNameFromInputs(data);
  return studyAreaName ? `Schematisation (${studyAreaName})` : undefined;
};

type DerivationRulesPerTag = {
  [T in ProcessTagSchemas["tag"]]?: FormDerivationRulesFor<ProcessRunFormData<T>>;
};

export const DERIVATION_RULES_PER_TAG: DerivationRulesPerTag = {
  define_study_area: {
    name: (data) => {
      const name = getStudyAreaNameInDefineStudyArea(data);
      return name ? `Define study area (${name})` : undefined;
    },
    outputs: {
      studyArea: {
        id: getStudyAreaNameInDefineStudyArea,
      },
    },
  },
  create_basic_hydro_data: {
    name: (data) => {
      const studyAreaName = getStudyAreaNameFromInputs(data);
      return studyAreaName ? `Create basic hydro data (${studyAreaName})` : undefined;
    },
  },
  create_2d_flood_model: {
    name: (data) => {
      const studyAreaName = getStudyAreaNameFromInputs(data);
      return studyAreaName ? `Create 2D flood model (${studyAreaName})` : undefined;
    },
    inputs: {
      schematisationName: getSchematisationNameFromStudyArea,
    },
    outputs: {
      schematisation: {
        id: getSchematisationNameFromStudyArea,
      },
    },
  },
};

export const getDerivationRulesPerTag = <T extends ProcessTagSchemas["tag"] | undefined>(
  tag: T
): FormDerivationRulesFor<ProcessRunFormData<T>> | undefined =>
  tag ? DERIVATION_RULES_PER_TAG[tag] : undefined;
